import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import DataTable from '../../components/DataTable';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  ArrowRightLeft,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  Eye,
  Trash2,
} from 'lucide-react';

const CoachMovementRequests = () => {
  const [requests, setRequests] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Dialogs
  const [createDialog, setCreateDialog] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    enrollment_id: '',
    new_program_id: '',
    new_class_id: '',
    reason: '',
    coach_notes: ''
  });
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState(null);

  const fetchData = async () => {
    try {
      const [requestsRes, enrollmentsRes, programsRes, classesRes] = await Promise.all([
        api.get('/movement-requests'),
        api.get('/enrollments'),
        api.get('/programs'),
        api.get('/classes')
      ]);
      setRequests(requestsRes.data);
      // Filter to only show current/active enrollments
      setEnrollments(enrollmentsRes.data.filter(e => 
        ['current', 'active', 'approved'].includes(e.status)
      ));
      setPrograms(programsRes.data.filter(p => p.is_active));
      setClasses(classesRes.data.filter(c => c.is_active));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getFilteredRequests = () => {
    if (activeTab === 'all') return requests;
    return requests.filter(r => r.status === activeTab);
  };

  const getStatusCounts = () => {
    const counts = { all: requests.length, pending: 0, approved: 0, rejected: 0 };
    requests.forEach(r => {
      if (counts[r.status] !== undefined) {
        counts[r.status]++;
      }
    });
    return counts;
  };

  const handleEnrollmentSelect = (enrollmentId) => {
    const enrollment = enrollments.find(e => e.id === enrollmentId);
    setSelectedEnrollment(enrollment);
    setFormData({
      ...formData,
      enrollment_id: enrollmentId,
      new_program_id: '',
      new_class_id: ''
    });
    setAvailableClasses([]);
  };

  const handleProgramSelect = (programId) => {
    setFormData({
      ...formData,
      new_program_id: programId,
      new_class_id: ''
    });
    // Filter classes for selected program
    const programClasses = classes.filter(c => c.program_id === programId);
    setAvailableClasses(programClasses);
  };

  const openCreateDialog = () => {
    setFormData({
      enrollment_id: '',
      new_program_id: '',
      new_class_id: '',
      reason: '',
      coach_notes: ''
    });
    setSelectedEnrollment(null);
    setAvailableClasses([]);
    setCreateDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.enrollment_id || !formData.new_program_id || !formData.new_class_id || !formData.reason.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/movement-requests', {
        enrollment_id: formData.enrollment_id,
        player_id: selectedEnrollment.player_id,
        current_program_id: selectedEnrollment.program_id,
        current_class_id: selectedEnrollment.class_id,
        new_program_id: formData.new_program_id,
        new_class_id: formData.new_class_id,
        reason: formData.reason,
        coach_notes: formData.coach_notes || null
      });
      toast.success('Movement request submitted successfully');
      setCreateDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (requestId) => {
    setRequestToCancel(requestId);
    setCancelDialogOpen(true);
  };

  const confirmCancel = async () => {
    if (!requestToCancel) return;
    try {
      await api.delete(`/movement-requests/${requestToCancel}`);
      toast.success('Request cancelled');
      setCancelDialogOpen(false);
      setRequestToCancel(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const columns = [
    {
      key: 'player_name',
      label: 'Player',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <div>
          <p className="font-medium text-slate-900">{row.player_name}</p>
          <p className="text-xs text-slate-500">{row.parent_name}</p>
        </div>
      )
    },
    {
      key: 'current',
      label: 'Current',
      render: (row) => (
        <div className="text-sm">
          <p className="font-medium">{row.current_program_name}</p>
          <p className="text-slate-500">{row.current_class_name || 'No class'}</p>
        </div>
      )
    },
    {
      key: 'new',
      label: 'Requested',
      render: (row) => (
        <div className="text-sm">
          <p className="font-medium text-blue-600">{row.new_program_name}</p>
          <p className="text-blue-500">{row.new_class_name}</p>
        </div>
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (row) => new Date(row.created_at).toLocaleDateString()
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => {
        const statusConfig = {
          pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700', icon: Clock },
          approved: { label: 'Approved', className: 'bg-green-100 text-green-700', icon: CheckCircle },
          rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700', icon: XCircle }
        };
        const config = statusConfig[row.status] || statusConfig.pending;
        const Icon = config.icon;
        return (
          <Badge className={`${config.className} flex items-center gap-1 w-fit`}>
            <Icon className="h-3 w-3" />
            {config.label}
          </Badge>
        );
      }
    }
  ];

  const actions = (row) => (
    <div className="flex items-center justify-end gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => { setSelectedRequest(row); setDetailsDialog(true); }}
        data-testid={`view-request-${row.id}`}
      >
        <Eye className="h-4 w-4" />
      </Button>
      {row.status === 'pending' && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleCancel(row.id)}
          className="text-red-600 hover:bg-red-50"
          data-testid={`cancel-request-${row.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <DashboardLayout title="Request Player Movement">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Request Player Movement">
      <div className="space-y-6" data-testid="coach-movement-requests-page">
        <div className="space-y-3">
          <p className="text-slate-600">Request to move players between programs or classes</p>
          <div className="flex flex-wrap items-center gap-2">
          <Button 
            onClick={openCreateDialog} 
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="new-request-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Button>
          </div>
        </div>

        <Card className="border border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-slate-600" />
              My Movement Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="flex w-full overflow-x-auto lg:w-auto lg:inline-flex mb-4">
                <TabsTrigger value="pending" className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-600" />
                  Pending ({statusCounts.pending})
                </TabsTrigger>
                <TabsTrigger value="approved" className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Approved ({statusCounts.approved})
                </TabsTrigger>
                <TabsTrigger value="rejected" className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  Rejected ({statusCounts.rejected})
                </TabsTrigger>
                <TabsTrigger value="all">
                  All ({statusCounts.all})
                </TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab}>
                <DataTable
                  data={getFilteredRequests()}
                  columns={columns}
                  actions={actions}
                  emptyIcon={ArrowRightLeft}
                  emptyMessage={activeTab === 'pending' ? "No pending movement requests." : `No ${activeTab} requests.`}
                  searchable={true}
                  searchFields={['player_name', 'parent_name', 'current_program_name', 'new_program_name']}
                  testIdPrefix="coach-movement"
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Create Request Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-blue-500" />
                Request Player Movement
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Select Enrollment */}
              <div className="space-y-2">
                <Label>Select Player/Enrollment *</Label>
                <Select
                  value={formData.enrollment_id}
                  onValueChange={handleEnrollmentSelect}
                >
                  <SelectTrigger data-testid="select-enrollment">
                    <SelectValue placeholder="Select a player enrollment..." />
                  </SelectTrigger>
                  <SelectContent>
                    {enrollments.map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.player_name} - {e.program_name} ({e.class_name || 'No class'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Current Info */}
              {selectedEnrollment && (
                <div className="p-3 bg-slate-100 rounded-lg text-sm">
                  <p className="text-xs text-slate-500 mb-1">CURRENT ENROLLMENT</p>
                  <p><strong>Player:</strong> {selectedEnrollment.player_name}</p>
                  <p><strong>Program:</strong> {selectedEnrollment.program_name}</p>
                  <p><strong>Class:</strong> {selectedEnrollment.class_name || 'None'}</p>
                  <p><strong>Fee:</strong> {cs()}{selectedEnrollment.monthly_fee}/mo</p>
                </div>
              )}

              {/* Select New Program */}
              <div className="space-y-2">
                <Label>New Program *</Label>
                <Select
                  value={formData.new_program_id}
                  onValueChange={handleProgramSelect}
                  disabled={!selectedEnrollment}
                >
                  <SelectTrigger data-testid="select-program">
                    <SelectValue placeholder="Select new program..." />
                  </SelectTrigger>
                  <SelectContent>
                    {programs.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} ({cs()}{p.monthly_fee}/mo)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Select New Class */}
              <div className="space-y-2">
                <Label>New Class *</Label>
                <Select
                  value={formData.new_class_id}
                  onValueChange={(value) => setFormData({...formData, new_class_id: value})}
                  disabled={!formData.new_program_id}
                >
                  <SelectTrigger data-testid="select-class">
                    <SelectValue placeholder="Select new class..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableClasses.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} ({c.current_enrollment || 0}/{c.max_capacity} enrolled)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Fee Change Preview */}
              {formData.new_program_id && selectedEnrollment && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm">
                    <strong>Fee Change:</strong>{' '}
                    {cs()}{selectedEnrollment.monthly_fee}/mo → {cs()}{programs.find(p => p.id === formData.new_program_id)?.monthly_fee}/mo
                  </p>
                </div>
              )}

              {/* Reason */}
              <div className="space-y-2">
                <Label>Reason for Move *</Label>
                <Textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  placeholder="Why should this player be moved? (e.g., skill level change, scheduling conflict)"
                  rows={3}
                  data-testid="reason-input"
                />
              </div>

              {/* Coach Notes */}
              <div className="space-y-2">
                <Label>Additional Notes (optional)</Label>
                <Textarea
                  value={formData.coach_notes}
                  onChange={(e) => setFormData({...formData, coach_notes: e.target.value})}
                  placeholder="Any additional notes for admin..."
                  rows={2}
                  data-testid="notes-input"
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialog(false)} disabled={submitting}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={submitting || !formData.enrollment_id || !formData.new_program_id || !formData.new_class_id || !formData.reason.trim()}
                  data-testid="submit-request-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Details Dialog */}
        <Dialog open={detailsDialog} onOpenChange={setDetailsDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-blue-500" />
                Request Details
              </DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4 mt-4">
                <Badge className={
                  selectedRequest.status === 'approved' ? 'bg-green-100 text-green-700' :
                  selectedRequest.status === 'rejected' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }>
                  {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
                </Badge>

                <div className="p-4 bg-slate-50 rounded-lg">
                  <p><strong>Player:</strong> {selectedRequest.player_name}</p>
                  <p><strong>Parent:</strong> {selectedRequest.parent_name}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-100 rounded-lg">
                    <p className="text-xs text-slate-500">FROM</p>
                    <p className="font-medium">{selectedRequest.current_program_name}</p>
                    <p className="text-sm">{selectedRequest.current_class_name}</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600">TO</p>
                    <p className="font-medium text-blue-700">{selectedRequest.new_program_name}</p>
                    <p className="text-sm text-blue-600">{selectedRequest.new_class_name}</p>
                  </div>
                </div>

                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm"><strong>Reason:</strong> {selectedRequest.reason}</p>
                  {selectedRequest.coach_notes && (
                    <p className="text-sm mt-2"><strong>Notes:</strong> {selectedRequest.coach_notes}</p>
                  )}
                </div>

                {selectedRequest.admin_notes && (
                  <div className={`p-3 rounded-lg ${selectedRequest.status === 'rejected' ? 'bg-red-50' : 'bg-green-50'}`}>
                    <p className="text-sm"><strong>Admin Response:</strong> {selectedRequest.admin_notes}</p>
                  </div>
                )}

                <p className="text-xs text-slate-500">
                  Submitted: {new Date(selectedRequest.created_at).toLocaleString()}
                  {selectedRequest.reviewed_at && (
                    <> | Reviewed: {new Date(selectedRequest.reviewed_at).toLocaleString()}</>
                  )}
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Cancel Confirmation Dialog */}
        <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Request</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to cancel this movement request?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep Request</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmCancel}
                className="bg-rose-500 hover:bg-rose-600"
              >
                Cancel Request
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default CoachMovementRequests;
